import { alpha } from '@mui/material/styles';

import { buildTooltipWrapper, escapeTooltipHtml } from '../shared/tooltip';
import type { EChartsTheme } from '../types';
import { comparisonHatchCss, COMPARISON_FILL_ALPHA } from './decal';
import { STUB_SERIES_PREFIX } from './series';
import type { ComparisonBarChartConfig, ComparisonDeltaSentimentColors, ComparisonTooltipRow } from './types';

type AxisParam = { seriesId?: string; dataIndex?: number };

/**
 * A swatch matching the bar it describes — solid for the current period, hatched for a comparison
 * range. Not `buildColorDot`: a dot cannot carry a hatch, and the hatch is the only thing telling
 * two bars of the same colour apart, so the legend has to show it.
 */
const buildSwatch = (color: string, rangeIndex: number): string => {
  const background =
    rangeIndex === 0 ? color : comparisonHatchCss(color, alpha(color, COMPARISON_FILL_ALPHA), rangeIndex);
  return `<div style="width:16px;height:16px;border-radius:2px;background:${background};flex-shrink:0;"></div>`;
};

/**
 * The arrow glyphs, matching the HTML chip column's MUI rounded arrow icons. An ECharts formatter
 * returns an HTML string, so it cannot render the React icon.
 *
 * Keyed by direction, not by delta kind: direction is category movement, and an expense that grew
 * points up even though its signed delta is negative.
 */
const DIRECTION_PATH = {
  up: 'M5.71 9.7c.39.39 1.02.39 1.41 0L11 5.83V21c0 .55.45 1 1 1s1-.45 1-1V5.83l3.88 3.88c.39.39 1.02.39 1.41 0s.39-1.02 0-1.41L12.7 2.7a.996.996 0 0 0-1.41 0L5.71 8.29c-.39.39-.39 1.03 0 1.41',
  down: 'M18.3 14.29a.996.996 0 0 0-1.41 0L13 18.17V3c0-.55-.45-1-1-1s-1 .45-1 1v15.18L7.12 14.3a.996.996 0 0 0-1.41 0c-.39.39-.39 1.02 0 1.41l5.59 5.59c.39.39 1.02.39 1.41 0l5.59-5.59c.38-.39.38-1.03 0-1.42',
} as const;

const buildDirectionIcon = (direction: NonNullable<ComparisonTooltipRow['deltaDirection']>): string =>
  `<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" style="display:block;flex-shrink:0;fill:currentColor;"><path d="${DIRECTION_PATH[direction]}"></path></svg>`;

const buildDeltaPill = (row: ComparisonTooltipRow, chipColors: ComparisonDeltaSentimentColors): string => {
  // An em dash means "there was nothing to compare"; a pill around it would imply a verdict.
  if (!row.deltaText || row.deltaText === '\u2014') return '';
  const { background, color } = chipColors[row.deltaSentiment];
  const icon = row.deltaDirection ? buildDirectionIcon(row.deltaDirection) : '';
  const percent = row.deltaPercentText
    ? `<span style="color:${color};font-size:14px;line-height:20px;font-weight:400;font-variant-numeric:tabular-nums;">${row.deltaPercentText}</span>`
    : '';
  return `<div style="display:flex;align-items:center;justify-content:flex-start;gap:8px;">
      <span style="display:inline-flex;align-items:center;gap:4px;height:28px;border-radius:8px;padding:0 8px;background:${background};color:${color};font-size:14px;line-height:20px;font-weight:400;font-variant-numeric:tabular-nums;box-sizing:border-box;">
        ${icon}${row.deltaText}
      </span>${percent}
    </div>`;
};

const buildComparisonTooltipHeader = (category: string, theme: EChartsTheme): string =>
  `<div style="color:${theme.textColor};font-size:14px;line-height:20px;font-weight:500;margin-bottom:8px;">${escapeTooltipHtml(category)}</div>`;

/**
 * The row tooltip: the category, then one line per range, aligned so the amounts form a column the
 * eye can compare down.
 *
 * Built from the shared tooltip primitives rather than `buildMultiSeriesGridBody`, which lays out a
 * dot/name/amount grid with no room for a hatched swatch.
 */
export const buildComparisonBarTooltipFormatter = (
  rows: ComparisonTooltipRow[],
  config: Pick<ComparisonBarChartConfig, 'ranges' | 'privacyMask' | 'chipColors'>,
  theme: EChartsTheme,
) => {
  const { ranges, privacyMask, chipColors } = config;

  return (params: unknown): string => {
    const list = (Array.isArray(params) ? params : [params]) as AxisParam[];

    // The zero-stub series is a marker, not data. It is `silent` and opts out of the tooltip, but
    // an axis trigger still collects it, so drop it here too rather than trusting one guard.
    const real = list.filter((param) => !param?.seriesId?.startsWith(STUB_SERIES_PREFIX));
    const dataIndex = real[0]?.dataIndex ?? list[0]?.dataIndex;
    const row = typeof dataIndex === 'number' ? rows[dataIndex] : undefined;
    if (!row) return '';

    const lines = ranges
      .map((range, rangeIndex) => {
        const value = row.values[rangeIndex];
        // An absent cell has no amount to report; saying "$0.00" would invent data.
        const amount = value == null ? '—' : String(privacyMask(row.tooltipAmounts[rangeIndex] ?? ''));
        const textColor = rangeIndex === 0 ? theme.textColor : theme.textSecondaryColor;
        return `<div style="display:grid;grid-template-columns:16px minmax(80px,1fr) auto;align-items:center;column-gap:16px;">
            ${buildSwatch(row.color, rangeIndex)}
            <span style="color:${textColor};font-size:14px;line-height:20px;font-weight:400;">${escapeTooltipHtml(range.label)}</span>
            <span style="color:${textColor};font-size:14px;line-height:20px;font-weight:500;text-align:right;white-space:nowrap;font-variant-numeric:tabular-nums;">${amount}</span>
          </div>`;
      })
      .join('');

    const delta = buildDeltaPill(row, chipColors);
    const difference = delta
      ? `<div style="height:1px;background:${theme.dividerColor};margin:8px 0;"></div>${delta}`
      : '';

    return buildTooltipWrapper(
      `${buildComparisonTooltipHeader(row.name, theme)}<div style="display:grid;row-gap:4px;min-width:240px;">${lines}</div>${difference}`,
    );
  };
};
